import type { CriminalWizardStep } from '../../services/criminalCaseMapper';
import type { CriminalSaveStep4 } from '../../types/criminalApi';
import type { AccusedRecord, CriminalWizardDraft } from '../../types/criminalCaseRequest';
import { deliveryMethodDisplay } from '../../utils/deliveryMethodDisplay';
import { formatDisplayDateTime } from '../../utils/dateFormat';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isCaseRequestUuid(id: string): boolean {
  return UUID_RE.test(id.trim());
}

export function formatSubmittedAtLabel(apiValue: string, fallback: string): string {
  const formatted = formatDisplayDateTime(apiValue);
  return formatted || fallback;
}

export type SubmitterDisplay = {
  agency: string;
  designation: string;
  name: string;
  email: string;
  contact: string;
  deliveryMethod: string;
};

export function submitterFromStep4(
  step4: CriminalSaveStep4 | undefined,
  draft: CriminalWizardDraft,
): SubmitterDisplay {
  const delivery = deliveryMethodDisplay(draft, [], step4);

  const submittedBy =
    step4?.submitted_by_display_name && step4.submitted_by_display_name.trim()
      ? step4.submitted_by_display_name.trim()
      : '-';

  return {
    agency: step4?.submitted_agency_name?.trim() || '—',
    designation: step4?.designation?.trim() || '—',
    name: submittedBy,
    email: step4?.email?.trim() || '—',
    contact: step4?.contact_no?.trim() || '—',
    deliveryMethod: delivery !== '—' ? delivery : '—',
  };
}

export const toSelectOpts = (items: { id: string; name: string }[]) =>
  items.map((x) => ({ id: x.id, label: x.name }));

function accusedHasData(rows: AccusedRecord[]): boolean {
  return rows.some((a) => a.name.trim() || a.status.trim() || a.statusOther.trim());
}

function step1HasData(d: CriminalWizardDraft): boolean {
  return !!(
    d.description.trim() ||
    d.caseSourceId.trim() ||
    d.caseSourceOther.trim() ||
    d.provinceId.trim() ||
    d.districtId.trim() ||
    d.highCourtId.trim() ||
    d.magistrateCourtId.trim() ||
    d.policeStationId.trim() ||
    d.yourReferenceNumber.trim() ||
    d.otherExternalReferences.trim() ||
    d.caseNumber.trim() ||
    d.hasConnectedMatters ||
    d.connectedCaseNumber.trim() ||
    d.connectedAgdFileRef.trim()
  );
}

function step2HasData(d: CriminalWizardDraft): boolean {
  return !!(d.victimName.trim() || d.victimDob.trim() || d.victimGenderId.trim());
}

function step3HasData(d: CriminalWizardDraft): boolean {
  return !!(
    d.offenceDateFrom.trim() ||
    d.offenceDateTo.trim() ||
    d.placeOfOffence.trim() ||
    d.urgencyLevelId.trim() ||
    d.deadline.trim() ||
    d.urgencyJustification.trim() ||
    !d.accusedDetailsAvailable ||
    accusedHasData(d.accused)
  );
}

function pickStep1Snapshot(d: CriminalWizardDraft) {
  return {
    description: d.description,
    caseSourceId: d.caseSourceId,
    caseSource: d.caseSource,
    caseSourceOther: d.caseSourceOther,
    provinceId: d.provinceId,
    districtId: d.districtId,
    highCourtId: d.highCourtId,
    magistrateCourtId: d.magistrateCourtId,
    policeStationId: d.policeStationId,
    yourReferenceNumber: d.yourReferenceNumber,
    otherExternalReferences: d.otherExternalReferences,
    caseNumber: d.caseNumber,
    hasConnectedMatters: d.hasConnectedMatters,
    connectedCaseNumber: d.connectedCaseNumber,
    connectedAgdFileRefAvailable: d.connectedAgdFileRefAvailable,
    connectedAgdFileRef: d.connectedAgdFileRef,
  };
}

function pickStep2Snapshot(d: CriminalWizardDraft) {
  return {
    victimName: d.victimName,
    victimDob: d.victimDob,
    victimGenderId: d.victimGenderId,
    victimGender: d.victimGender,
  };
}

function pickStep3Snapshot(d: CriminalWizardDraft) {
  return {
    offenceDateFrom: d.offenceDateFrom,
    offenceDateTo: d.offenceDateTo,
    placeOfOffence: d.placeOfOffence,
    accusedDetailsAvailable: d.accusedDetailsAvailable,
    urgencyLevelId: d.urgencyLevelId,
    deadline: d.deadline,
    urgencyJustification: d.urgencyJustification,
    accused: d.accused.map((a) => ({
      name: a.name,
      status: a.status,
      statusOther: a.statusOther,
    })),
  };
}

function stepHasUnsavedChanges(
  step: 1 | 2 | 3,
  draft: CriminalWizardDraft,
  initial: CriminalWizardDraft,
): boolean {
  const pickers = { 1: pickStep1Snapshot, 2: pickStep2Snapshot, 3: pickStep3Snapshot } as const;
  return JSON.stringify(pickers[step](draft)) !== JSON.stringify(pickers[step](initial));
}

/**
 * After a per-step Case Update save, merge only that step's fields into the shared
 * `initial` baseline so other steps keep their dirty/unsaved Save state.
 */
export function mergeSavedStepIntoInitialBaseline<T extends object>(
  prevInitial: T,
  savedDraft: T,
  pickStepSnapshot: (d: T) => object,
  cloneDraft: (d: T) => T,
  metaKeys: readonly string[] = ['draftRequestId', 'referenceNo'],
): T {
  const merged = cloneDraft(prevInitial);
  const fromSaved = cloneDraft(savedDraft);
  const mergedRecord = merged as Record<string, unknown>;
  const savedRecord = fromSaved as Record<string, unknown>;
  for (const key of Object.keys(pickStepSnapshot(savedDraft))) {
    if (Object.prototype.hasOwnProperty.call(savedRecord, key)) {
      mergedRecord[key] = savedRecord[key];
    }
  }
  for (const key of metaKeys) {
    if (Object.prototype.hasOwnProperty.call(savedRecord, key)) {
      mergedRecord[key] = savedRecord[key];
    }
  }
  return merged;
}

export function mergeCriminalSavedStepIntoInitial(
  step: 1 | 2 | 3,
  prevInitial: CriminalWizardDraft,
  savedDraft: CriminalWizardDraft,
  cloneDraft: (d: CriminalWizardDraft) => CriminalWizardDraft,
): CriminalWizardDraft {
  const pickers = { 1: pickStep1Snapshot, 2: pickStep2Snapshot, 3: pickStep3Snapshot } as const;
  return mergeSavedStepIntoInitialBaseline(prevInitial, savedDraft, pickers[step], cloneDraft);
}

/** Save is allowed only when the step has data and differs from the last loaded snapshot. */
export function canSaveCaseUpdateStep(
  step: CriminalWizardStep,
  draft: CriminalWizardDraft | null,
  initial: CriminalWizardDraft | null,
): boolean {
  if (!draft || !initial || step > 3) return false;
  const s = step as 1 | 2 | 3;
  const hasDataFns = { 1: step1HasData, 2: step2HasData, 3: step3HasData } as const;
  const hasData = hasDataFns[s](draft) || hasDataFns[s](initial);
  return hasData && stepHasUnsavedChanges(s, draft, initial);
}

/** After confirming cancel on Case Update, reset fields, close dialog, and navigate away. */
export function confirmCaseUpdateCancel(
  resetSection: () => void,
  closeDialog: () => void,
  onCancelConfirmed?: () => void,
): void {
  resetSection();
  closeDialog();
  onCancelConfirmed?.();
}
